import { CryptoEngineV2 } from "./CryptoEngineV2";
import { CryptoInitializationError, CryptoValidationError } from "./cryptoErrors";

const MASTER_KEY_STORAGE_KEY = "sia.benchmarking.masterKey";
const VERSION_STORAGE_KEY = "sia.benchmarking.cryptoVersion";
let activeEngine = null;
let operationQueue = Promise.resolve();
let revision = 0;
const listeners = new Set();

const readSession = () => ({
  masterKey: sessionStorage.getItem(MASTER_KEY_STORAGE_KEY) || "",
  version: sessionStorage.getItem(VERSION_STORAGE_KEY) || "",
});
const SESSION_STORAGE_KEYS = [MASTER_KEY_STORAGE_KEY, VERSION_STORAGE_KEY, "isMasterKeyValid"];
const clearSession = () => {
  let firstError = null;
  SESSION_STORAGE_KEYS.forEach((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      firstError ||= error;
      try {
        sessionStorage.setItem(key, "");
      } catch (fallbackError) {
        firstError ||= fallbackError;
      }
    }
  });
  if (firstError) throw firstError;
};
const storeSession = (engine) => {
  try {
    sessionStorage.setItem(MASTER_KEY_STORAGE_KEY, engine.masterKey);
    sessionStorage.setItem(VERSION_STORAGE_KEY, engine.version);
    sessionStorage.removeItem("isMasterKeyValid");
  } catch (error) {
    try {
      clearSession();
    } catch (cleanupError) {
      console.error("EncryptionProvider failed to clean up an incomplete stored session", cleanupError);
    }
    throw error;
  }
};
const snapshot = () =>
  Object.freeze({
    revision,
    authenticated: Boolean(activeEngine),
    engineVersion: activeEngine?.version || null,
    companyHash: activeEngine?.companyHash || null,
  });
const publish = () => {
  const current = snapshot();
  listeners.forEach((listener) => {
    try {
      listener(current);
    } catch (error) {
      listeners.delete(listener);
      console.error("EncryptionProvider subscriber failed", error);
    }
  });
  return current;
};

export class EncryptionProvider {
  static currentEngineType = CryptoEngineV2;
  static previousEngineType = null;

  static getSnapshot() {
    return snapshot();
  }

  static requireSnapshot() {
    const current = snapshot();
    if (!current.authenticated || !current.companyHash)
      throw new CryptoInitializationError("EncryptionProvider: crypto session is not initialized");
    return current;
  }

  static subscribe(listener) {
    if (typeof listener !== "function") throw new TypeError("EncryptionProvider subscriber must be a function");
    listeners.add(listener);
    try {
      listener(snapshot());
    } catch (error) {
      listeners.delete(listener);
      throw error;
    }
    return () => listeners.delete(listener);
  }

  static captureRevision() {
    return revision;
  }


  static assertRevision(expected) {
    if (expected !== revision) throw new CryptoInitializationError("Crypto session changed while the operation was running");
  }


  static runExclusive(operation) {
    const result = operationQueue.then(operation, operation);
    operationQueue = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }

  static async createEngine(EngineType, masterKey) {
    if (!EngineType) throw new CryptoInitializationError("EncryptionProvider: missing engine type");
    const engine = new EngineType(masterKey);
    try {
      await engine.ready();
      return engine;
    } catch (error) {
      engine.purge?.();
      throw error;
    }
  }

  static createCurrentEngine(masterKey) {
    return this.createEngine(this.currentEngineType, masterKey);
  }


  static getEngineType(version) {
    return [this.currentEngineType, this.previousEngineType].find((EngineType) => EngineType?.VERSION === version) || null;
  }

  static hasStoredSession() {
    return Boolean(readSession().masterKey);
  }

  static restoreSession() {
    const expected = revision;
    return this.runExclusive(async () => {
      if (activeEngine) return true;
      this.assertRevision(expected);
      const { masterKey, version } = readSession();
      if (!masterKey) return false;
      const EngineType = this.getEngineType(version) || (!version ? this.previousEngineType : null);
      if (!EngineType) {
        clearSession();
        return false;
      }
      let restoredEngine = null;
      try {
        restoredEngine = await this.createEngine(EngineType, masterKey);
        this.activate(restoredEngine, expected);
        return true;
      } catch (error) {
        if (restoredEngine && restoredEngine !== activeEngine) {
          try {
            restoredEngine.purge?.();
          } catch (cleanupError) {
            console.error("EncryptionProvider failed to purge a stale restored engine", cleanupError);
          }
        }
        if (revision === expected && !activeEngine) clearSession();
        throw error;
      }
    });
  }

  static async identify(masterKey, validationDelegate, expected = revision) {
    if (!masterKey) throw new CryptoInitializationError("EncryptionProvider: missing master key");
    if (typeof validationDelegate !== "function") throw new CryptoValidationError("EncryptionProvider: missing validation delegate");
    const engineTypes = [this.currentEngineType, this.previousEngineType].filter(
      (type, index, types) => type && types.indexOf(type) === index
    );
    let invalidResponse = null;

    for (const EngineType of engineTypes) {
      this.assertRevision(expected);
      const candidate = await this.createEngine(EngineType, masterKey);
      try {
        const result = await validationDelegate(candidate);
        this.assertRevision(expected);
        if (result?.valid === true) return { ...result, valid: true, engine: candidate, version: candidate.version };
        if (result?.valid !== false) throw new CryptoValidationError(`Unexpected validation result for ${candidate.version}`);
        invalidResponse ||= result.response;
        candidate.purge();
      } catch (error) {
        candidate.purge();
        throw error;
      }
    }
    return { valid: false, engine: null, response: invalidResponse || { status: 200, data: { valid: false } } };
  }

  static activate(engine, expected = revision) {
    this.assertRevision(expected);
    if (!engine?.masterKey || !engine?.version || !engine?.companyHash)
      throw new CryptoInitializationError("EncryptionProvider: cannot activate an invalid engine");
    const previous = activeEngine;
    try {
      storeSession(engine);
    } catch (error) {
      if (engine !== previous) {
        try {
          engine.purge?.();
        } catch (cleanupError) {
          console.error("EncryptionProvider failed to purge an unactivated engine", cleanupError);
        }
      }
      throw error;
    }
    activeEngine = engine;
    revision += 1;
    if (previous && previous !== engine) {
      try {
        previous.purge?.();
      } catch (error) {
        console.error("EncryptionProvider failed to purge the previous engine", error);
      }
    }
    publish();
    return engine;
  }

  static getCapabilities() {
    if (!activeEngine) throw new CryptoInitializationError("EncryptionProvider: crypto session is not initialized");
    const engine = activeEngine;
    const expectedRevision = revision;
    const invoke = async (method, value) => {
      this.assertRevision(expectedRevision);
      const result = await engine[method](value);
      this.assertRevision(expectedRevision);
      return result;
    };
    return Object.freeze({
      version: engine.version,
      companyHash: engine.companyHash,
      encryptEmployee: (value) => invoke("encryptEmployee", value),
      decryptEmployee: (value) => invoke("decryptEmployee", value),
    });
  }

  static isInitialized() {
    return Boolean(activeEngine);
  }

  static purgeIfRevision(expectedRevision) {
    if (revision !== expectedRevision) return false;
    this.purge();
    return true;
  }

  static purge() {
    revision += 1;
    const engine = activeEngine;
    activeEngine = null;
    try {
      engine?.purge?.();
    } catch (error) {
      console.error("EncryptionProvider failed to purge the active engine", error);
    }
    try {
      clearSession();
    } catch (error) {
      console.error("EncryptionProvider failed to clear the stored session", error);
    }
    publish();
  }
}
